import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import WorkbenchShell from "../WorkbenchShell";
import QuoteOutcomeDialog from "./QuoteOutcomeDialog";
import { getRafterTailTrial } from "../../../services/apiService";
import {
  STATUS_LABELS,
  finishLabel,
  hours,
  modelLabel,
  money,
  shortDate,
  tierLabel,
  winRate,
} from "./trialFormat";
import "./trial.css";

/**
 * `/trial` — the rafter-tail pricing trial.
 *
 * Every quote priced on the trial model, what happened to it, and the review
 * numbers Andy asked for. Open quotes sit in their own table and never count
 * as losses: win rate is won ÷ (won + lost).
 */

function Stat({ label, value, hint, tone }) {
  return (
    <div
      className={`trial-stat${tone ? ` trial-stat--${tone}` : ""}`}
      role="group"
      aria-label={label}
    >
      <span className="trial-stat__k">{label}</span>
      <span className="trial-stat__v">{value}</span>
      {hint ? <span className="trial-stat__hint">{hint}</span> : null}
    </div>
  );
}

function StatusPill({ status }) {
  return (
    <span className={`trial-pill trial-pill--${status}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function JobCell({ row }) {
  return (
    <td className="trial-job">
      <Link to={`/jobs/${row.job_id}/quote`} className="trial-job__name">
        {row.job_name || "Untitled job"}
      </Link>
      <span className="trial-job__meta">
        {row.customer_name || "No customer"} · quoted{" "}
        {shortDate(row.created_at)} · {modelLabel(row.model_version)}
      </span>
    </td>
  );
}

function ActualsCell({ row }) {
  const parts = [];
  if (row.actual_material_cost) {
    parts.push(`${money(row.actual_material_cost)} material`);
  }
  const shop = hours(row.actual_shop_hours);
  if (shop) parts.push(`${shop} shop`);
  return (
    <td className="trial-actuals" title={row.actual_notes || undefined}>
      {parts.length ? (
        parts.map((part) => <span key={part}>{part}</span>)
      ) : (
        <span className="trial-none">—</span>
      )}
    </td>
  );
}

function QuoteTable({ rows, decided, onEdit }) {
  return (
    <div className="trial-table-wrap">
      <table className={`trial-table${decided ? " trial-table--decided" : ""}`}>
        <thead>
          <tr>
            <th scope="col">Job</th>
            <th scope="col">Tier</th>
            <th scope="col" className="trial-num">
              Tails
            </th>
            <th scope="col" className="trial-num trial-wide-head">
              Rafter tails quoted
            </th>
            <th scope="col" className="trial-num">
              Discount
            </th>
            {decided ? (
              <>
                <th scope="col">Result</th>
                <th scope="col" className="trial-num">
                  Accepted
                </th>
                <th scope="col">Why we lost it</th>
                <th scope="col">Actual cost</th>
              </>
            ) : null}
            <th scope="col" className="trial-actions">
              <span className="sr-only">Update</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.job_id}>
              <JobCell row={row} />
              <td>{tierLabel(row.tier)}</td>
              <td className="trial-num">
                {row.quantity}
                <span className="trial-sub">
                  {finishLabel(row.finish_options)}
                </span>
              </td>
              <td className="trial-num">
                {money(row.quoted_before_tax)}
                {row.job_has_other_items ? (
                  <span
                    className="trial-sub"
                    title="The job also quoted other lines or delivery — this figure is the rafter tails only, before tax."
                  >
                    + other items on job
                  </span>
                ) : null}
              </td>
              <td className="trial-num">
                {row.discount ? (
                  money(row.discount)
                ) : (
                  <span className="trial-none">—</span>
                )}
              </td>
              {decided ? (
                <>
                  <td>
                    <StatusPill status={row.status} />
                    <span className="trial-sub">
                      {row.decision_date
                        ? shortDate(row.decision_date)
                        : "No date"}
                    </span>
                  </td>
                  <td className="trial-num">
                    {row.status === "won" && row.accepted_price ? (
                      money(row.accepted_price)
                    ) : (
                      <span className="trial-none">—</span>
                    )}
                  </td>
                  <td
                    className="trial-reason"
                    title={row.loss_reason || undefined}
                  >
                    {row.status !== "lost" ? (
                      <span className="trial-none">—</span>
                    ) : row.loss_reason ? (
                      row.loss_reason
                    ) : (
                      <span className="trial-none">Not given</span>
                    )}
                  </td>
                  <ActualsCell row={row} />
                </>
              ) : null}
              <td className="trial-actions">
                <button
                  type="button"
                  className="wb-tbtn wb-tbtn--edge"
                  onClick={() => onEdit(row)}
                  aria-label={`Update ${row.job_name || "quote"}`}
                  title="Record the result"
                >
                  <span aria-hidden="true">⋮</span>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TierTable({ tiers }) {
  return (
    <div className="trial-table-wrap">
      <table className="trial-table trial-table--tiers">
        <thead>
          <tr>
            <th scope="col">Customer tier</th>
            <th scope="col" className="trial-num">
              Quotes
            </th>
            <th scope="col" className="trial-num">
              Won
            </th>
            <th scope="col" className="trial-num">
              Lost
            </th>
            <th scope="col" className="trial-num">
              Still open
            </th>
            <th scope="col" className="trial-num">
              Win rate
            </th>
            <th scope="col" className="trial-num">
              Rafter tails quoted
            </th>
            <th scope="col" className="trial-num">
              Accepted
            </th>
            <th scope="col" className="trial-num">
              Discounts
            </th>
          </tr>
        </thead>
        <tbody>
          {tiers.map((tier) => (
            <tr key={tier.tier || "none"}>
              <td>{tierLabel(tier.tier)}</td>
              <td className="trial-num">{tier.issued}</td>
              <td className="trial-num">{tier.won}</td>
              <td className="trial-num">{tier.lost}</td>
              <td className="trial-num">{tier.open}</td>
              <td className="trial-num">{winRate(tier.win_rate)}</td>
              <td className="trial-num">{money(tier.quoted_before_tax)}</td>
              <td className="trial-num">{money(tier.accepted)}</td>
              <td className="trial-num">{money(tier.discounts)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function tierMix(entries, countKey) {
  if (!entries?.length) return "none";
  return entries
    .map((entry) => `${tierLabel(entry.tier)} ${entry[countKey]}`)
    .join(", ");
}

function PriorPeriod({ prior, summary }) {
  if (!prior) return null;
  if (!prior.available) {
    return <p className="trial-note">{prior.note}</p>;
  }
  return (
    <div className="trial-compare">
      <div className="trial-compare__col">
        <span className="trial-stat__k">This range</span>
        <p>
          <b>{summary.issued}</b> trial{" "}
          {summary.issued === 1 ? "quote" : "quotes"}, <b>{summary.quantity}</b>{" "}
          tails
        </p>
        <p className="trial-compare__mix">
          Tiers: {tierMix(summary.by_tier, "issued")}
        </p>
      </div>
      <div className="trial-compare__col">
        <span className="trial-stat__k">
          The {prior.days} days before ({shortDate(prior.from)} –{" "}
          {shortDate(prior.to)})
        </span>
        <p>
          <b>{prior.rafter_tail_jobs}</b> rafter-tail{" "}
          {prior.rafter_tail_jobs === 1 ? "job" : "jobs"} priced the old way,{" "}
          <b>{prior.rafter_tail_quantity}</b> tails
        </p>
        <p className="trial-compare__mix">
          Tiers: {tierMix(prior.by_tier, "jobs")}
        </p>
      </div>
      <p className="trial-note trial-compare__note">{prior.note}</p>
    </div>
  );
}

function RafterTailTrialPage() {
  const [range, setRange] = useState({ from: "", to: "" });
  const [applied, setApplied] = useState({ from: "", to: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);

  // Only the newest request may paint the page: a slow response for an older
  // date range must never land under the newer range's caption.
  const latestRequest = useRef(0);
  const load = useCallback(async (filter, { keepShown = false } = {}) => {
    const request = ++latestRequest.current;
    setLoading(true);
    setError(null);
    // A new date range never shows the old range's numbers under its caption,
    // not even while it loads. Reloading the same range after a save keeps them.
    if (!keepShown) setData(null);
    try {
      const next = await getRafterTailTrial(filter);
      if (request === latestRequest.current) setData(next);
    } catch (err) {
      if (request === latestRequest.current) {
        // Never leave the previous range's numbers under this range's error.
        setData(null);
        setError(err?.error || err?.message || "Could not load the trial.");
      }
    } finally {
      if (request === latestRequest.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(applied);
  }, [applied, load]);

  const handleApply = (event) => {
    event.preventDefault();
    setApplied({ ...range });
  };

  const handleClear = () => {
    setRange({ from: "", to: "" });
    setApplied({ from: "", to: "" });
  };

  const handleSaved = () => {
    setEditing(null);
    load(applied, { keepShown: true });
  };

  const rows = data?.rows || [];
  const openRows = rows.filter((row) => row.status === "open");
  const decidedRows = rows.filter((row) => row.status !== "open");
  const summary = data?.summary;
  const decidedCount = summary ? summary.won + summary.lost : 0;

  const center = (
    <div className="trial-page">
      <p className="wb-eyebrow">Rafter-tail pricing trial</p>
      <h1 className="wb-h1">How the trial quotes are doing</h1>
      <p className="wb-sub trial-caveat">
        A few weeks of quotes is an early signal, not a conclusive pricing test.
        Open quotes are kept separate and are never counted as losses.
      </p>

      <form className="trial-filter" onSubmit={handleApply}>
        <label>
          <span>From</span>
          <input
            type="date"
            className="wb-input"
            value={range.from}
            onChange={(event) =>
              setRange((current) => ({ ...current, from: event.target.value }))
            }
          />
        </label>
        <label>
          <span>To</span>
          <input
            type="date"
            className="wb-input"
            value={range.to}
            onChange={(event) =>
              setRange((current) => ({ ...current, to: event.target.value }))
            }
          />
        </label>
        <button type="submit" className="wb-tbtn wb-tbtn--solid">
          Show
        </button>
        <button
          type="button"
          className="wb-tbtn wb-tbtn--edge"
          onClick={handleClear}
          disabled={!applied.from && !applied.to && !range.from && !range.to}
        >
          All dates
        </button>
        <span className="trial-hint trial-filter__hint">
          {applied.from || applied.to
            ? `Quotes created ${applied.from ? shortDate(applied.from) : "any time"} – ${
                applied.to ? shortDate(applied.to) : "today"
              }`
            : "Showing every trial quote"}
        </span>
      </form>

      {error ? (
        <p className="trial-error" role="alert">
          {error}
        </p>
      ) : null}

      {loading && !data ? (
        <p className="trial-note">Loading trial quotes…</p>
      ) : null}

      {summary ? (
        <>
          <section className="trial-stats" aria-label="Trial summary">
            <Stat label="Quotes issued" value={summary.issued} />
            <Stat label="Won" value={summary.won} tone="won" />
            <Stat label="Lost" value={summary.lost} tone="lost" />
            <Stat label="Still open" value={summary.open} />
            <Stat
              label="Win rate"
              value={winRate(summary.win_rate)}
              hint={
                decidedCount
                  ? `${summary.won} of ${decidedCount} decided`
                  : "No decisions yet"
              }
              tone="emphasis"
            />
            <Stat label="Accepted" value={money(summary.accepted)} />
            <Stat label="Discounts" value={money(summary.discounts)} />
          </section>

          {summary.by_tier.length ? (
            <section className="trial-section">
              <h2 className="trial-h2">By customer tier</h2>
              <TierTable tiers={summary.by_tier} />
            </section>
          ) : null}

          <section className="trial-section">
            <h2 className="trial-h2">Compared with before the trial</h2>
            <PriorPeriod prior={data.prior_period} summary={summary} />
          </section>

          <section className="trial-section" aria-label="Still open quotes">
            <h2 className="trial-h2">
              Still open <span className="trial-count">{openRows.length}</span>
            </h2>
            {openRows.length ? (
              <QuoteTable rows={openRows} onEdit={setEditing} />
            ) : (
              <p className="trial-note">
                {rows.length
                  ? "Every trial quote in this range has a result."
                  : "No trial quotes yet. A quote joins the trial when a rafter tail on it is priced with the trial model."}
              </p>
            )}
          </section>

          <section className="trial-section" aria-label="Decided quotes">
            <h2 className="trial-h2">
              Decided <span className="trial-count">{decidedRows.length}</span>
            </h2>
            {decidedRows.length ? (
              <QuoteTable rows={decidedRows} decided onEdit={setEditing} />
            ) : (
              <p className="trial-note">
                No won or lost quotes yet. Use Update on an open quote once the
                customer decides.
              </p>
            )}
          </section>
        </>
      ) : null}

      <QuoteOutcomeDialog
        row={editing}
        onClose={() => setEditing(null)}
        onSaved={handleSaved}
      />
    </div>
  );

  return (
    <WorkbenchShell
      variant="estimate"
      title="Pricing trial"
      subtitle="Rafter tails"
      center={center}
      centerHeader={{ label: "Trial quotes" }}
      inspector={null}
      statusBar={null}
    />
  );
}

export default RafterTailTrialPage;
